'use client';

import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { Button } from '../../components/button';
import { Collapsible } from '../../components/collapsible';
import { AlertBanner } from '../../components/alert-banner';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  ATTACK_VECTOR,
  ATTACK_COMPLEXITY,
  PRIVILEGES_REQUIRED,
  USER_INTERACTION,
  SCOPE,
  IMPACT_METRICS,
  CVSS4_ATTACK_VECTOR,
  CVSS4_ATTACK_COMPLEXITY,
  CVSS4_ATTACK_REQUIREMENTS,
  CVSS4_PRIVILEGES_REQUIRED,
  CVSS4_USER_INTERACTION,
  CVSS4_IMPACT_METRICS,
} from './constants';
import type { CVSSMetricKey } from './constants';
import type {
  CvssCalculatorProps,
  Cvss4MetricKey,
  Cvss4Metrics,
  CvssMetricKey,
  CvssMetrics,
} from './types';
import { useCvssCalculator } from './hooks';
import { MetricSelector } from './components/MetricSelector';
import { EnvironmentalMetricsPanel } from './components/EnvironmentalMetricsPanel';
import { ScoreSummaryRow } from './components/ScoreSummaryRow';
import { MetricColumns } from './components/MetricColumns';

type MetricRow<TKey extends string> = {
  field: TKey;
  title: string;
  options: readonly {
    label: string;
    value: string;
  }[];
  metricKey: CVSSMetricKey;
};

const getScoreColor = (score: string): string => {
  if (!score) return 'fg.muted';
  const parsed = parseFloat(score);

  if (parsed >= 9.0) return 'fg.error';
  if (parsed >= 7.0) return 'fg.default';
  if (parsed >= 4.0) return 'fg.default';

  return 'fg.muted';
};

export function CvssCalculator({
  initialScore,
  initialVector,
  initialEnvironmentalVector,
  version: versionProp,
  findingVersion,
  environmentalRequirements,
  onScoreChange,
  copilotChrome,
}: CvssCalculatorProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isVersionMismatchDismissed, setIsVersionMismatchDismissed] =
    useState(false);
  const t = useTranslations('designSystem.cvss.calculator');
  const tCommon = useTranslations('designSystem.common');
  const {
    version,
    appVersion,
    metrics,
    calculatedScore,
    vectorString,
    hasEnvironmentalRequirements,
    environmentalRequirementsForDisplay,
    includeModifiedEnvironmentalMetrics,
    environmentalDisplay,
    handleMetricChange,
    switchToAppVersion,
  } = useCvssCalculator({
    initialScore,
    initialVector,
    initialEnvironmentalVector,
    version: versionProp,
    environmentalRequirements,
    onScoreChange,
  });

  useEffect(() => {
    setIsVersionMismatchDismissed(false);
  }, [findingVersion, appVersion]);
  useEffect(() => {
    const metricFooterCount = copilotChrome?.metricFooters
      ? Object.keys(copilotChrome.metricFooters).length
      : 0;

    if (metricFooterCount > 0) {
      setIsExpanded(true);
    }
  }, [copilotChrome?.metricFooters]);
  const showVersionMismatchAlert =
    findingVersion != null &&
    findingVersion !== appVersion &&
    !isVersionMismatchDismissed;
  const isV4 = version === '4.0';
  const cvss3Metrics = metrics as CvssMetrics;
  const cvss4Metrics = metrics as Cvss4Metrics;
  const cvss4LeftMetrics: MetricRow<Cvss4MetricKey>[] = [
    {
      field: 'attackVector',
      title: t('metrics40.attackVector'),
      options: CVSS4_ATTACK_VECTOR,
      metricKey: 'attackVector',
    },
    {
      field: 'attackComplexity',
      title: t('metrics40.attackComplexity'),
      options: CVSS4_ATTACK_COMPLEXITY,
      metricKey: 'attackComplexity',
    },
    {
      field: 'attackRequirements',
      title: t('metrics40.attackRequirements'),
      options: CVSS4_ATTACK_REQUIREMENTS,
      metricKey: 'attackRequirements',
    },
    {
      field: 'privilegesRequired',
      title: t('metrics40.privilegesRequired'),
      options: CVSS4_PRIVILEGES_REQUIRED,
      metricKey: 'privilegesRequired',
    },
    {
      field: 'userInteraction',
      title: t('metrics40.userInteraction'),
      options: CVSS4_USER_INTERACTION,
      metricKey: 'userInteraction40',
    },
  ];
  const cvss4RightMetrics: MetricRow<Cvss4MetricKey>[] = [
    {
      field: 'vulnConfidentiality',
      title: t('metrics40.vulnConfidentiality'),
      options: CVSS4_IMPACT_METRICS,
      metricKey: 'confidentiality',
    },
    {
      field: 'vulnIntegrity',
      title: t('metrics40.vulnIntegrity'),
      options: CVSS4_IMPACT_METRICS,
      metricKey: 'integrity',
    },
    {
      field: 'vulnAvailability',
      title: t('metrics40.vulnAvailability'),
      options: CVSS4_IMPACT_METRICS,
      metricKey: 'availability',
    },
    {
      field: 'subConfidentiality',
      title: t('metrics40.subConfidentiality'),
      options: CVSS4_IMPACT_METRICS,
      metricKey: 'confidentiality',
    },
    {
      field: 'subIntegrity',
      title: t('metrics40.subIntegrity'),
      options: CVSS4_IMPACT_METRICS,
      metricKey: 'subsequentIntegrity',
    },
    {
      field: 'subAvailability',
      title: t('metrics40.subAvailability'),
      options: CVSS4_IMPACT_METRICS,
      metricKey: 'subsequentAvailability',
    },
  ];
  const cvss3LeftMetrics: MetricRow<CvssMetricKey>[] = [
    {
      field: 'attackVector',
      title: t('metrics.attackVector'),
      options: ATTACK_VECTOR,
      metricKey: 'attackVector',
    },
    {
      field: 'attackComplexity',
      title: t('metrics.attackComplexity'),
      options: ATTACK_COMPLEXITY,
      metricKey: 'attackComplexity',
    },
    {
      field: 'privilegesRequired',
      title: t('metrics.privilegesRequired'),
      options: PRIVILEGES_REQUIRED,
      metricKey: 'privilegesRequired',
    },
    {
      field: 'userInteraction',
      title: t('metrics.userInteraction'),
      options: USER_INTERACTION,
      metricKey: 'userInteraction',
    },
  ];
  const cvss3RightMetrics: MetricRow<CvssMetricKey>[] = [
    {
      field: 'scope',
      title: t('metrics.scope'),
      options: SCOPE,
      metricKey: 'scope',
    },
    {
      field: 'confidentiality',
      title: t('metrics.confidentiality'),
      options: IMPACT_METRICS,
      metricKey: 'confidentiality',
    },
    {
      field: 'integrity',
      title: t('metrics.integrity'),
      options: IMPACT_METRICS,
      metricKey: 'integrity',
    },
    {
      field: 'availability',
      title: t('metrics.availability'),
      options: IMPACT_METRICS,
      metricKey: 'availability',
    },
  ];

  const metricFooters = copilotChrome?.metricFooters;
  const renderMetricColumn = <TKey extends CvssMetricKey | Cvss4MetricKey>(
    rows: MetricRow<TKey>[],
    values: Partial<Record<TKey, string>>,
  ) =>
    rows.map(({ field, title, options, metricKey }) => (
      <MetricSelector
        key={field}
        title={title}
        options={options}
        value={values[field] ?? ''}
        onChange={(value) => handleMetricChange(field, value)}
        metricKey={metricKey}
        footer={metricFooters?.[field]}
      />
    ));

  return (
    <VStack gap={0} w="full" alignItems="stretch">
      {showVersionMismatchAlert ? (
        <Box mb={2} w="full">
          <AlertBanner
            kind="info"
            type="outline"
            description={
              hasEnvironmentalRequirements
                ? `${t('versionMismatch', {
                    findingVersion,
                    appVersion,
                  })} ${t('versionMismatchEnvironmentalNote')}`
                : t('versionMismatch', {
                    findingVersion,
                    appVersion,
                  })
            }
            action={
              <Button
                variant="ghost"
                size="xs"
                color="fg.muted"
                onClick={switchToAppVersion}
              >
                {t('switchToAppVersion', { appVersion })}
              </Button>
            }
            onDismiss={() => setIsVersionMismatchDismissed(true)}
            dismissAriaLabel={tCommon('dismiss')}
          />
        </Box>
      ) : null}

      <Box
        data-copilot-review-control=""
        position="relative"
        w="full"
        mb={2}
        borderWidth="1px"
        borderStyle="solid"
        borderColor={
          copilotChrome?.isReviewing ? 'border.accent.default' : 'transparent'
        }
        borderRadius="md"
        transition="border-color 220ms ease"
      >
        <ScoreSummaryRow
          baseScoreLabel={t('cvssScore')}
          baseScore={calculatedScore}
          baseScoreColor={getScoreColor(calculatedScore)}
          vectorLabel={
            hasEnvironmentalRequirements
              ? t('environmentalVector')
              : t('cvssVector')
          }
          vector={
            hasEnvironmentalRequirements
              ? (environmentalDisplay?.vector ?? '')
              : vectorString
          }
          emptyVectorLabel={t('selectMetricsBelow')}
          environmentalScoreLabel={
            hasEnvironmentalRequirements ? t('environmentalScore') : undefined
          }
          environmentalScore={
            hasEnvironmentalRequirements
              ? (environmentalDisplay?.score ?? '')
              : undefined
          }
          environmentalScoreColor={
            hasEnvironmentalRequirements
              ? getScoreColor(environmentalDisplay?.score ?? '')
              : undefined
          }
          baseScoreFooter={copilotChrome?.baseScoreFooter}
          vectorFooter={copilotChrome?.vectorFooter}
        />
      </Box>
      {copilotChrome?.groupAlert ? (
        <Box mt={2} mb={2} w="full">
          {copilotChrome.groupAlert}
        </Box>
      ) : null}

      <Collapsible.Root
        open={isExpanded}
        onOpenChange={({ open }) => setIsExpanded(open)}
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          w="full"
          mb={2}
        >
          <Collapsible.Trigger asChild>
            <Button
              size="sm"
              variant="ghost"
              fontWeight="medium"
              justifyContent="flex-start"
              px={0}
              _hover={{ bg: 'transparent' }}
            >
              <HStack gap={2}>
                {isExpanded ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
                <Text fontSize="xs" fontWeight="medium">
                  {isV4 ? t('title40') : t('title')}
                </Text>
              </HStack>
            </Button>
          </Collapsible.Trigger>
        </HStack>

        <Collapsible.Content>
          <VStack gap={4} w="full" pt={2}>
            {isV4 ? (
              <MetricColumns
                left={renderMetricColumn(cvss4LeftMetrics, cvss4Metrics)}
                right={renderMetricColumn(cvss4RightMetrics, cvss4Metrics)}
              />
            ) : (
              <MetricColumns
                left={renderMetricColumn(cvss3LeftMetrics, cvss3Metrics)}
                right={renderMetricColumn(cvss3RightMetrics, cvss3Metrics)}
              />
            )}

            <Text fontSize="2xs" color="fg.muted" textAlign="center">
              {t('clickButtonsInstruction')}
            </Text>

            {hasEnvironmentalRequirements &&
            environmentalRequirementsForDisplay ? (
              <EnvironmentalMetricsPanel
                requirements={environmentalRequirementsForDisplay}
                version={isV4 ? '4.0' : '3.1'}
                includeModifiedMetrics={includeModifiedEnvironmentalMetrics}
              />
            ) : null}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </VStack>
  );
}
